import { useState } from 'react'
import { ClipboardList, Pencil, Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { ActionGate } from '@/components/shared/action-gate'
import type { Questionnaire } from '@/gen/cortado/v1/questionnaires_pb'
import { formatQuestionnaireAnswer, itemHasAnswer, sortedQuestionnaireItems } from '@/lib/booking-questionnaire'
import { formatShortDate } from '@/lib/date-utils'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { SheetLayout } from '@/components/shared/sheet-layout'
import { BookingQuestionnaireEditorSheet } from './booking-questionnaire-editor-sheet'

interface BookingQuestionnaireCardProps {
  organizationId: string
  bookingId: string
  eventTypeKey?: string
  questionnaire?: Questionnaire
  /** When the questionnaire reached the customer (Booking.questionnaire_sent_at). */
  sentAt?: Timestamp
  /** When the customer last sent answers (Booking.questionnaire_returned_at). */
  returnedAt?: Timestamp
  /** Sheet layout for the editor; 'pipeline' on pipeline pages. */
  layout?: SheetLayout
}

/** "Returned Oct 14", "Sent Oct 12" or nothing, for the card's sub-line. */
export function questionnaireDeliveryLabel(sentAt?: Timestamp, returnedAt?: Timestamp): string | undefined {
  if (returnedAt) return `Returned ${formatShortDate(returnedAt)}`
  if (sentAt) return `Sent ${formatShortDate(sentAt)}`
  return undefined
}

export function BookingQuestionnaireCard({
  organizationId,
  bookingId,
  eventTypeKey,
  questionnaire,
  sentAt,
  returnedAt,
  layout,
}: BookingQuestionnaireCardProps) {
  const delivery = questionnaireDeliveryLabel(sentAt, returnedAt)
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const items = sortedQuestionnaireItems(questionnaire)

  if (items.length === 0) {
    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle>
              <div className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                Questionnaire
              </div>
            </CardTitle>
            <CardDescription>No questions have been added to this booking yet.</CardDescription>
          </CardHeader>
          <CardContent>
            <Empty className="min-h-[6rem] p-4">
              <EmptyMedia variant="icon">
                <ClipboardList />
              </EmptyMedia>
              <EmptyDescription>
                Add questions to collect details for this booking.
              </EmptyDescription>
              <ActionGate
                organizationId={organizationId}
                permission="EventsManage"
                verb="add"
                noun="a questionnaire"
              >
                <Button type="button" size="sm" variant="outline" onClick={() => setIsEditorOpen(true)}>
                  <Plus data-icon="inline-start" />
                  Add questionnaire
                </Button>
              </ActionGate>
            </Empty>
          </CardContent>
        </Card>

        <BookingQuestionnaireEditorSheet
          layout={layout}
          organizationId={organizationId}
          bookingId={bookingId}
          eventTypeKey={eventTypeKey}
          questionnaire={questionnaire}
          open={isEditorOpen}
          onOpenChange={setIsEditorOpen}
        />
      </>
    )
  }

  const answeredCount = items.filter(itemHasAnswer).length

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle>
              <div className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                Questionnaire
              </div>
            </CardTitle>
            <ActionGate
              organizationId={organizationId}
              permission="EventsManage"
              verb="edit"
              noun="the questionnaire"
            >
              <Button type="button" size="sm" variant="outline" onClick={() => setIsEditorOpen(true)}>
                <Pencil data-icon="inline-start" />
                Edit
              </Button>
            </ActionGate>
          </div>
          <CardDescription>
            {answeredCount} of {items.length} answered
            {delivery && ` · ${delivery}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {items.map((item) => {
              const answer = formatQuestionnaireAnswer(item)
              return (
                <div key={item.id} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{item.prompt}</p>
                    {item.required && (
                      <Badge variant="outline">
                        Required
                      </Badge>
                    )}
                  </div>
                  {answer ? (
                    <p className="text-sm text-muted-foreground">{answer}</p>
                  ) : (
                    <p className="text-sm italic text-muted-foreground">No answer yet</p>
                  )}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      <BookingQuestionnaireEditorSheet
        layout={layout}
        organizationId={organizationId}
        bookingId={bookingId}
        eventTypeKey={eventTypeKey}
        questionnaire={questionnaire}
        open={isEditorOpen}
        onOpenChange={setIsEditorOpen}
      />
    </>
  )
}
